import { useEffect, useRef, useState, useId } from "react";
import {
  dynamicParameterProject,
  dynamicSourceCanSkip,
  requestedDynamicSources,
  resolveSourceParameters,
  type Project,
  type SourceParameters,
  type Values,
} from "@proof/core";
import { FormGenerator } from "./FormGenerator";

/** Parameter values remain separate from document variables; the host resolves resulting data. */
export function DynamicSourceParameters({
  project,
  partId,
  values = {},
  onChange,
}: {
  project: Project;
  partId?: string;
  values?: SourceParameters;
  onChange: (values: SourceParameters, forceRefresh: boolean) => void;
}) {
  const sources = requestedDynamicSources(project, partId);
  const [open, setOpen] = useState(false),
    [draft, setDraft] = useState<SourceParameters>({}),
    [validate, setValidate] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    if (open && dialog.current && !dialog.current.open)
      dialog.current.showModal();
  }, [open]);
  if (!sources.length) return null;
  const edit = () => {
    setDraft(
      Object.fromEntries(
        sources.map((source) => [
          source.id,
          values[source.id] === null &&
          dynamicSourceCanSkip(project, source.id, partId)
            ? null
            : Object.fromEntries(
                source.parameters
                  .filter((parameter) =>
                    Object.hasOwn(values[source.id] || {}, parameter.key),
                  )
                  .map((parameter) => [
                    parameter.key,
                    values[source.id]![parameter.key],
                  ]),
              ),
        ]),
      ),
    );
    setValidate(false);
    setOpen(true);
  };
  const apply = (force: boolean) => {
    try {
      const parsed = resolveSourceParameters(project, draft, partId);
      onChange(parsed || {}, force);
      setOpen(false);
    } catch {
      setValidate(true);
    }
  };
  return (
    <div className="dynamic-source-parameters">
      <button
        type="button"
        className="button"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={edit}
      >
        Source parameters
      </button>
      <button
        type="button"
        className="button"
        onClick={() => {
          try {
            onChange(
              resolveSourceParameters(project, values, partId) || {},
              true,
            );
          } catch {
            edit();
          }
        }}
      >
        Refresh source data
      </button>
      {open && (
        <dialog
          ref={dialog}
          aria-labelledby={titleId}
          onCancel={() => setOpen(false)}
          style={{
            width: "min(620px, 90vw)",
            maxHeight: "85dvh",
            overflow: "auto",
            border: "1px solid #ccc",
            borderRadius: 12,
            padding: 24,
          }}
        >
          <form
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              apply(false);
            }}
          >
            <h2 id={titleId}>Source parameters</h2>
            <p>
              Choose the information used to load available options. Review the
              proof after loading.
            </p>
            {sources.map((source) => (
              <section key={source.id}>
                <h3>{source.name}</h3>
                {dynamicSourceCanSkip(project, source.id, partId) ? (
                  <label>
                    <input
                      type="checkbox"
                      checked={draft[source.id] === null}
                      onChange={(event) =>
                        setDraft((current) => ({
                          ...current,
                          [source.id]: event.target.checked ? null : {},
                        }))
                      }
                    />
                    Skip {source.name}
                  </label>
                ) : (
                  <p className="muted">
                    This data source is required for this document or part.
                  </p>
                )}
                {draft[source.id] === null && (
                  <p role="status">
                    Configured values will be used without loading this source.
                  </p>
                )}
                <fieldset disabled={draft[source.id] === null}>
                  <FormGenerator
                    project={dynamicParameterProject(project, source)}
                    values={
                      (Object.hasOwn(draft, source.id) &&
                      draft[source.id] !== null
                        ? draft[source.id]!
                        : {}) as Values
                    }
                    onChange={(value) =>
                      setDraft((current) => ({
                        ...current,
                        [source.id]: value as NonNullable<
                          SourceParameters[string]
                        >,
                      }))
                    }
                    showValidation={validate && draft[source.id] !== null}
                    compact
                  />
                </fieldset>
              </section>
            ))}
            {validate && (
              <p role="alert">Enter valid values for the source parameters.</p>
            )}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 12,
                marginTop: 20,
              }}
            >
              <button
                type="button"
                className="button"
                onClick={() => setOpen(false)}
              >
                Cancel
              </button>
              <button type="submit" className="button primary">
                Load source data
              </button>
              <button
                type="button"
                className="button"
                onClick={() => apply(true)}
              >
                Refresh and load
              </button>
            </div>
          </form>
        </dialog>
      )}
    </div>
  );
}
